import { z } from "zod";
import type { Database } from "./database";
import type { UrlActiveFilters } from "@/lib/url-active-filters";
import {
  countUrlActiveFilters,
  getUrlFilterMode,
  getUrlFilterMultiValues,
  getUrlFilterSlugValue,
  isUrlFilterMultiEntry,
  isUrlFilterSlugEntry,
  isUrlSlugFilterKey,
  parseUrlActiveFilters,
  serializeUrlActiveFilters,
} from "@/lib/url-active-filters";

export type {
  UrlActiveFilters,
  UrlFilterEntry,
  UrlFilterMode,
  UrlFilterMultiEntry,
  UrlFilterSlugEntry,
} from "@/lib/url-active-filters";

export {
  countUrlActiveFilters,
  getUrlFilterMode,
  getUrlFilterMultiValues,
  getUrlFilterSlugValue,
  isUrlFilterMultiEntry,
  isUrlFilterSlugEntry,
  isUrlSlugFilterKey,
  parseUrlActiveFilters,
  serializeUrlActiveFilters,
};

const filterOptionListSchema = z.array(z.string()).default([]);

/** RPC metrics: null / undefined / "" → 0; invalid values → 0 */
export const gscMetricSchema = z.preprocess(
  (val) => (val == null || val === "" ? 0 : val),
  z.coerce.number().catch(0),
);

/**
 * Normalize get_gsc_filter_options JSON.
 * Backend currently returns plural `domains` (and may omit singular `domain`).
 */
const normalizeGscFilterOptions = (raw: unknown): unknown => {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return raw;
  const source = raw as Record<string, unknown>;

  const pickList = (...keys: string[]): unknown => {
    for (const key of keys) {
      if (key in source) return source[key];
    }
    return [];
  };

  return {
    domain: pickList("domain", "domains"),
    country: pickList("country", "countries"),
    device: pickList("device", "devices"),
    geo: pickList("geo", "geos"),
    site_type: pickList("site_type", "site_types"),
    language: pickList("language", "languages"),
    brand_keyword: pickList("brand_keyword", "brand_keywords"),
  };
};

/** `get_gsc_filter_options` RPC payload (`Returns: Json`). */
export const gscFilterOptionsSchema = z.preprocess(
  normalizeGscFilterOptions,
  z.object({
    domain: filterOptionListSchema,
    country: filterOptionListSchema,
    device: filterOptionListSchema,
    geo: filterOptionListSchema,
    site_type: filterOptionListSchema,
    language: filterOptionListSchema,
    brand_keyword: filterOptionListSchema,
  }),
);

export type GscFilterOptions = z.infer<typeof gscFilterOptionsSchema>;

export const EMPTY_GSC_FILTER_OPTIONS: GscFilterOptions = {
  domain: [],
  country: [],
  device: [],
  geo: [],
  site_type: [],
  language: [],
  brand_keyword: [],
};

export type GetGscFilterOptionsArgs =
  Database["public"]["Functions"]["get_gsc_filter_options"]["Args"];

export type GetGscKpiSummaryArgs =
  Database["public"]["Functions"]["get_gsc_kpi_summary"]["Args"];

export type GscKpiSummary =
  Database["public"]["Functions"]["get_gsc_kpi_summary"]["Returns"][number];

export const gscKpiSummarySchema = z.object({
  clicks: gscMetricSchema,
  impressions: gscMetricSchema,
  ctr: gscMetricSchema,
  avg_position: gscMetricSchema,
  prev_clicks: gscMetricSchema,
  prev_impressions: gscMetricSchema,
  prev_ctr: gscMetricSchema,
  prev_avg_position: gscMetricSchema,
});

export const EMPTY_GSC_KPI_SUMMARY: GscKpiSummary = {
  clicks: 0,
  impressions: 0,
  ctr: 0,
  avg_position: 0,
  prev_clicks: 0,
  prev_impressions: 0,
  prev_ctr: 0,
  prev_avg_position: 0,
};

export type GetGscTrendArgs =
  Database["public"]["Functions"]["get_gsc_trend"]["Args"];

export type GscTrendGranularity = "day" | "week" | "month";

export const GSC_TREND_GRANULARITIES = [
  "day",
  "week",
  "month",
] as const satisfies readonly GscTrendGranularity[];

export type GscTrendPoint =
  Database["public"]["Functions"]["get_gsc_trend"]["Returns"][number];

export const gscTrendPointSchema = z.object({
  bucket_date: z.string().default(""),
  domain: z.string().default(""),
  clicks: gscMetricSchema,
  impressions: gscMetricSchema,
  ctr: gscMetricSchema,
  avg_position: gscMetricSchema,
});

export type GetGscDetailArgs =
  Database["public"]["Functions"]["get_gsc_detail"]["Args"];

export type GscDetailRow =
  Database["public"]["Functions"]["get_gsc_detail"]["Returns"][number];

export const GSC_DETAIL_SORT_COLUMNS = [
  "domain",
  "clean_url",
  "keyword",
  "country",
  "device",
  "record_date",
  "clicks",
  "impressions",
  "ctr",
  "avg_position",
] as const;

export type GscDetailSortColumn = (typeof GSC_DETAIL_SORT_COLUMNS)[number];

export type GscSortDirection = "asc" | "desc";

export const GSC_PAGE_SIZES = [15, 50, 100] as const;

export type GscPageSize = (typeof GSC_PAGE_SIZES)[number];

export const GSC_DEFAULT_PAGE_SIZE: GscPageSize = 15;

export const gscDetailRowSchema = z.object({
  domain: z.string().default(""),
  clean_url: z.string().default(""),
  keyword: z.string().default(""),
  country: z.string().default(""),
  device: z.string().default(""),
  record_date: z.string().default(""),
  clicks: gscMetricSchema,
  impressions: gscMetricSchema,
  ctr: gscMetricSchema,
  avg_position: gscMetricSchema,
  prev_clicks: gscMetricSchema,
  prev_impressions: gscMetricSchema,
  prev_ctr: gscMetricSchema,
  prev_avg_position: gscMetricSchema,
  total_count: gscMetricSchema,
});

export const gscDetailResponseSchema = z.array(gscDetailRowSchema);

export type GscDetailResponse = z.infer<typeof gscDetailResponseSchema>;

export const EMPTY_GSC_DETAIL: GscDetailResponse = [];

export interface GscDetailResult {
  rows: GscDetailRow[];
  totalCount: number;
}

export const extractGscDetailTotalCount = (
  rows: GscDetailRow[] | null | undefined,
): number => rows?.[0]?.total_count ?? 0;

export type GetGscDomainCompareArgs =
  Database["public"]["Functions"]["get_gsc_domain_compare"]["Args"];

/**
 * `get_gsc_domain_overview` args — sourced from Supabase codegen (`database.ts`).
 */
export type GetGscDomainOverviewArgs =
  Database["public"]["Functions"]["get_gsc_domain_overview"]["Args"];

export type GscDomainOverviewRow =
  Database["public"]["Functions"]["get_gsc_domain_overview"]["Returns"][number];

export const gscDomainOverviewRowSchema = z.object({
  domain: z.string().default(""),
  clicks: gscMetricSchema,
  impressions: gscMetricSchema,
  ctr: gscMetricSchema,
  avg_position: gscMetricSchema,
  total_count: gscMetricSchema,
});

export const gscDomainOverviewResponseSchema = z.array(
  gscDomainOverviewRowSchema,
);

export type GscDomainOverviewResponse = z.infer<
  typeof gscDomainOverviewResponseSchema
>;

export const EMPTY_GSC_DOMAIN_OVERVIEW: GscDomainOverviewResponse = [];

export interface GscDomainOverviewResult {
  rows: GscDomainOverviewRow[];
  totalCount: number;
}

export const EMPTY_GSC_DOMAIN_OVERVIEW_RESULT: GscDomainOverviewResult = {
  rows: [],
  totalCount: 0,
};

export const extractGscDomainOverviewTotalCount = (
  rows: GscDomainOverviewRow[] | null | undefined,
): number => rows?.[0]?.total_count ?? 0;

export const GSC_DOMAIN_OVERVIEW_SORT_COLUMNS = [
  "domain",
  "clicks",
  "impressions",
  "ctr",
  "avg_position",
] as const;

export type GscDomainOverviewSortColumn =
  (typeof GSC_DOMAIN_OVERVIEW_SORT_COLUMNS)[number];

export const isGscDomainOverviewSortColumn = (
  value: string,
): value is GscDomainOverviewSortColumn =>
  (GSC_DOMAIN_OVERVIEW_SORT_COLUMNS as readonly string[]).includes(value);

export const GSC_MAX_COMPARE_DOMAINS = 5;

export const GSC_COMPARE_METRICS = [
  "clicks",
  "impressions",
  "ctr",
  "position",
] as const;

export type GscCompareMetric = (typeof GSC_COMPARE_METRICS)[number];

export const GSC_DEFAULT_COMPARE_METRIC: GscCompareMetric = "clicks";

export const isGscCompareMetric = (value: string): value is GscCompareMetric =>
  (GSC_COMPARE_METRICS as readonly string[]).includes(value);

export const GSC_COMPARE_METRIC_LABELS: Record<GscCompareMetric, string> = {
  clicks: "Clicks",
  impressions: "Impressions",
  ctr: "CTR",
  position: "Position",
};

export const gscCompareDomainSummarySchema = gscKpiSummarySchema.extend({
  domain: z.string().default(""),
});

/** `get_gsc_domain_compare` RPC payload (`Returns: Json`) — array of per-domain KPI cards. */
export const gscDomainCompareResponseSchema = z.array(
  gscCompareDomainSummarySchema,
);

export type GscCompareDomainSummary = z.infer<
  typeof gscCompareDomainSummarySchema
>;
export type GscDomainCompareResponse = GscCompareDomainSummary[];

export const EMPTY_GSC_DOMAIN_COMPARE: GscDomainCompareResponse = [];

/** URL `filters` JSON keys — must match RPC `p_filters` object keys. */
export const GSC_FILTER_KEYS = {
  domain: "domain",
  country: "country",
  device: "device",
  query: "query",
  slug: "slug",
  geo: "geo",
  site_type: "site_type",
  brand_keyword: "brand_keyword",
  language: "language",
} as const;

export type GscFilterKey =
  (typeof GSC_FILTER_KEYS)[keyof typeof GSC_FILTER_KEYS];

/** GSC Overview / Detail date presets (see `GscDateRangeControls`). */
export type GscDateRangeOption =
  | "yesterday"
  | "last_7_days"
  | "last_28_days"
  | "last_90_days"
  | "custom";

export const GSC_DEFAULT_DATE_RANGE: GscDateRangeOption = "last_28_days";

export const GSC_DATE_RANGE_OPTIONS = [
  "yesterday",
  "last_7_days",
  "last_28_days",
  "last_90_days",
  "custom",
] as const satisfies readonly GscDateRangeOption[];

export const GSC_DATE_RANGE_LABELS: Record<GscDateRangeOption, string> = {
  yesterday: "Yesterday",
  last_7_days: "Last 7 days",
  last_28_days: "Last 28 days",
  last_90_days: "Last 90 days",
  custom: "Custom",
};

export interface GscResolvedDateRange {
  dateFrom: string;
  dateTo: string;
}

export const isGscDetailSortColumn = (
  value: string,
): value is GscDetailSortColumn =>
  (GSC_DETAIL_SORT_COLUMNS as readonly string[]).includes(value);

export const isGscPageSize = (value: number): value is GscPageSize =>
  (GSC_PAGE_SIZES as readonly number[]).includes(value);

export const isGscTrendGranularity = (
  value: string,
): value is GscTrendGranularity =>
  (GSC_TREND_GRANULARITIES as readonly string[]).includes(value);

export const isGscDateRangeOption = (
  value: string,
): value is GscDateRangeOption =>
  (GSC_DATE_RANGE_OPTIONS as readonly string[]).includes(value);

/** GSC `p_filters` keys that use text-search `{ mode, value }` entries (contains). */
export const GSC_TEXT_SEARCH_FILTER_KEYS = new Set<string>([
  GSC_FILTER_KEYS.slug,
  GSC_FILTER_KEYS.query,
]);

export const isGscTextSearchFilterKey = (key: string): boolean =>
  GSC_TEXT_SEARCH_FILTER_KEYS.has(key);

export const parseGscActiveFiltersFromUrl = (url: URL): UrlActiveFilters =>
  parseUrlActiveFilters(url.searchParams.get("filters"));
